import { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import questionMarkCircleFill from '@iconify/icons-eva/question-mark-circle-fill';
import playCircleFill from '@iconify/icons-eva/play-circle-fill';
import navigation2Fill from '@iconify/icons-eva/navigation-2-fill';
import bookOpenFill from '@iconify/icons-eva/book-open-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import MenuPopover from '../MenuPopover';
import HelpDrawer from './help/HelpDrawer';
import { useTour } from './TourProvider';

export default function HelpButton() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const {
    start,
    availableTours,
    hasSeen,
    hasUnseen,
    resetAll,
    unavailableTourId,
    clearUnavailable,
    guides,
    startGuide,
    stopGuide,
    activeGuide,
  } = useTour();
  const { enqueueSnackbar } = useSnackbar();

  // A tour whose targets are all absent would otherwise look like a dead click.
  useEffect(() => {
    if (!unavailableTourId) return;
    enqueueSnackbar('That tour needs an open schedule. Open one and try again.', {
      variant: 'info',
    });
    clearUnavailable();
  }, [unavailableTourId, enqueueSnackbar, clearUnavailable]);

  const handleStart = (tourId) => {
    setOpen(false);
    start(tourId);
  };

  const handleOpenDrawer = () => {
    setOpen(false);
    setIsDrawerOpen(true);
  };

  return (
    <>
      <Tooltip title="Help">
        <IconButton
          ref={anchorRef}
          data-tour="help-button"
          onClick={() => setOpen(true)}
          sx={{
            width: 40,
            height: 40,
            color: 'text.primary',
            ...(open && { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) }),
          }}
        >
          <Badge color="primary" variant="dot" invisible={!hasUnseen}>
            <Icon icon={questionMarkCircleFill} width={22} height={22} />
          </Badge>
        </IconButton>
      </Tooltip>

      <MenuPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        sx={{ width: 320, py: 1 }}
      >
        {availableTours.length > 0 && (
          <>
            <Typography
              variant="overline"
              sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}
            >
              Tours for this page
            </Typography>
            {availableTours.map((tour) => (
              <MenuItem
                key={tour.id}
                onClick={() => handleStart(tour.id)}
                sx={{ py: 1, whiteSpace: 'normal', alignItems: 'flex-start' }}
              >
                <ListItemIcon sx={{ color: 'primary.main', mt: 0.25 }}>
                  <Icon icon={playCircleFill} width={20} height={20} />
                </ListItemIcon>
                <ListItemText
                  primary={tour.title}
                  secondary={`${tour.steps.length} steps${hasSeen(tour) ? '' : ' · new'}`}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { whiteSpace: 'normal' },
                  }}
                />
              </MenuItem>
            ))}
            <Divider sx={{ my: 1 }} />
          </>
        )}

        <Typography
          variant="overline"
          sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}
        >
          Walk me through it
        </Typography>
        {guides.map((item) => {
          const isRunning = activeGuide?.id === item.id;
          return (
            <MenuItem
              key={item.id}
              onClick={() => {
                setOpen(false);
                if (isRunning) stopGuide();
                else startGuide(item.id);
              }}
              sx={{ py: 1, whiteSpace: 'normal', alignItems: 'flex-start' }}
            >
              <ListItemIcon sx={{ color: isRunning ? 'warning.main' : 'primary.main', mt: 0.25 }}>
                <Icon icon={navigation2Fill} width={20} height={20} />
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                secondary={
                  isRunning
                    ? `Step ${activeGuide.stage + 1} of ${activeGuide.stageCount} · stop`
                    : item.summary
                }
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: { whiteSpace: 'normal' },
                }}
              />
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleOpenDrawer} sx={{ py: 1 }}>
          <ListItemIcon>
            <Icon icon={bookOpenFill} width={20} height={20} />
          </ListItemIcon>
          <ListItemText primary="Help centre" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        <MenuItem
          onClick={() => {
            resetAll();
            setOpen(false);
          }}
          sx={{ py: 1 }}
        >
          <ListItemIcon>
            <Icon icon={refreshFill} width={20} height={20} />
          </ListItemIcon>
          <ListItemText primary="Replay tours" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        {availableTours.length === 0 && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              No tours for this page yet.
            </Typography>
          </Box>
        )}
      </MenuPopover>

      <HelpDrawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </>
  );
}
